import {
  List,
  Datagrid,
  TextField,
  DateField,
  ImageField,
  SelectField,
  FilterButton,
  TextInput,
  SelectInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  BulkDeleteButton,
  useRecordContext,
  EditButton,
  DeleteButton,
} from "react-admin";
import { Box, Chip, Card, CardMedia, Typography } from "@mui/material";
import { Image, Video, FileImage } from "lucide-react";

// Category choices
const categoryChoices = [
  { id: "portfolio", name: "Portfolio" },
  { id: "hero", name: "Hero" },
  { id: "testimonial", name: "Testimonial" },
  { id: "team", name: "Team" },
  { id: "service", name: "Service" },
  { id: "blog", name: "Blog" },
  { id: "gallery", name: "Gallery" },
  { id: "other", name: "Other" },
];

// Resource type choices
const resourceTypeChoices = [
  { id: "image", name: "Image" },
  { id: "video", name: "Video" },
];

// Filters for the list
const mediaFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn />,
  <SelectInput
    key="category"
    source="category"
    label="Category"
    choices={categoryChoices}
  />,
  <SelectInput
    key="resourceType"
    source="resourceType"
    label="Type"
    choices={resourceTypeChoices}
  />,
];

// Custom thumbnail field that handles both images and videos
const ThumbnailField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isVideo = record.resourceType === "video";
  const thumbnailUrl = isVideo
    ? record.secureUrl.replace(/\.[^/.]+$/, ".jpg") // Video thumbnail
    : record.secureUrl;

  return (
    <Card sx={{ width: 120, height: 80, position: "relative" }}>
      <CardMedia
        component="img"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        image={thumbnailUrl}
        alt={record.title || "Media thumbnail"}
      />
      {isVideo && (
        <Box
          sx={{
            position: "absolute",
            bottom: 4,
            right: 4,
            bgcolor: "rgba(0,0,0,0.7)",
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
          }}
        >
          <Video size={14} color="white" />
        </Box>
      )}
    </Card>
  );
};

// Custom type field with icon
const TypeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isVideo = record.resourceType === "video";
  return (
    <Chip
      icon={isVideo ? <Video size={14} /> : <Image size={14} />}
      label={isVideo ? "Video" : "Image"}
      size="small"
      color={isVideo ? "secondary" : "primary"}
      variant="outlined"
    />
  );
};

// Custom file size field
const FileSizeField = () => {
  const record = useRecordContext();
  if (!record || !record.bytes) return <span>—</span>;

  const bytes = record.bytes;
  if (bytes < 1024) return <span>{bytes} B</span>;
  if (bytes < 1024 * 1024) return <span>{(bytes / 1024).toFixed(1)} KB</span>;
  return <span>{(bytes / (1024 * 1024)).toFixed(1)} MB</span>;
};

// Custom dimensions field
const DimensionsField = () => {
  const record = useRecordContext();
  if (!record || !record.width || !record.height) return <span>—</span>;
  return <span>{record.width} × {record.height}</span>;
};

// List actions toolbar
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

// Bulk action buttons
const MediaBulkActionButtons = () => (
  <>
    <BulkDeleteButton />
  </>
);

/**
 * MediaList - Displays all media items in a datagrid with filters and actions
 */
export const MediaList = () => {
  return (
    <List
      filters={mediaFilters}
      actions={<ListActions />}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
      sx={{
        "& .RaList-main": {
          backgroundColor: "#1a1a2e",
        },
      }}
    >
      <Datagrid
        bulkActionButtons={<MediaBulkActionButtons />}
        rowClick="edit"
        sx={{
          "& .RaDatagrid-headerCell": {
            backgroundColor: "#16213e",
            color: "#22d3ee",
            fontWeight: 600,
          },
          "& .RaDatagrid-row": {
            "&:hover": {
              backgroundColor: "rgba(34, 211, 238, 0.05)",
            },
          },
        }}
      >
        <ThumbnailField />
        <TextField source="title" label="Title" />
        <TypeField />
        <SelectField
          source="category"
          label="Category"
          choices={categoryChoices}
        />
        <DimensionsField />
        <FileSizeField />
        <DateField source="createdAt" label="Uploaded" showTime />
        <EditButton />
        <DeleteButton />
      </Datagrid>
    </List>
  );
};

export default MediaList;

